import { cartsQuery } from '@/api/carts/cartQueries';
import { Button } from '@/components/common/Button';
import { Pagination } from '@/components/common/Pagination';
import { Spinner } from '@/components/common/Spinner';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/dashboard/DashboardCard';
import { CartRow } from '@/components/dashboard/my-carts/CartRow';
import { MyCartsWrapper } from '@/components/dashboard/my-carts/MyCartsWrapper';
import { CartState } from '@/domain/Cart';
import { routes } from '@/routes/routes';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

export const MyCartsPage = () => {
  const { data: cartsData, isLoading } = useQuery(cartsQuery);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 6;

  const allCarts = useMemo(
    () =>
      cartsData?.data.filter((cart) => cart.state !== CartState.APPROVED) || [],
    [cartsData?.data],
  );

  const paginatedCarts = useMemo(
    () => allCarts.slice((currentPage - 1) * perPage, currentPage * perPage),
    [allCarts, currentPage],
  );

  useEffect(() => {
    const maxPage = Math.ceil(allCarts.length / perPage);
    if (currentPage > maxPage && maxPage > 0) setCurrentPage(1);
  }, [allCarts.length, currentPage, perPage]);

  return (
    <MyCartsWrapper>
      {isLoading && (
        <Card className="flex min-h-40 w-full items-center justify-center">
          <Spinner className="size-10" />
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Customize orders</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="flex flex-col gap-4">
            <Table>
              <TableHeader className="hidden lg:table-header-group">
                <TableRow>
                  <TableHead>Order name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {paginatedCarts.map((cart) => (
                  <CartRow key={cart.id} cart={cart} />
                ))}
              </TableBody>
            </Table>

            {allCarts.length > perPage && (
              <Pagination
                currentPage={currentPage}
                lastPage={Math.ceil(allCarts.length / perPage)}
                total={allCarts.length}
                perPage={perPage}
                onPageChange={setCurrentPage}
                isLoading={isLoading}
              />
            )}
          </div>

          <Button asChild className="mt-3 w-fit">
            <Link to={routes.dashboard.myCarts.create}>Add another order</Link>
          </Button>
        </CardContent>
      </Card>
    </MyCartsWrapper>
  );
};
